import React, { useCallback, useMemo, useRef, useState } from "react";

import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteValue,
  createFilterOptions,
  TextField,
  TextFieldProps
} from "@mui/material";
import { isNull } from "lodash";
import { Nullable } from "store/types";

import { useDidUpdate } from "../../../hooks";

// import { useDidUpdate } from "hooks";

type ItemRole = "item" | "category";

const selectAllVar = "SELECTALL";

type SingleValue = SelectOption | string | number;

type Value = AutocompleteValue<SingleValue, boolean, true, false>;

type State = AutocompleteValue<SingleValue, true, false, false>;

const wrapInArray = (value: Value): State => (Array.isArray(value) ? value : [value]);

const toPrimitive = (item: SingleValue): string | number => (typeof item === "object" ? item.value : item);

// const toObject = (item: string | number): SelectOption => ({
//   key: typeof item === "number" ? String(item) : item,
//   value: item,
//   text: typeof item === "number" ? String(item) : item,
// });

export type SelectOption<T = string | number> = {
  key: string;
  value: T;
  text: string;
  role?: ItemRole;
};

const setDefaultValue = (
  options: AutocompleteMultiSelectProps["options"],
  defaultValue?: AutocompleteMultiSelectProps["defaultValue"]
): Value => {
  if (!defaultValue) {
    return [];
  }

  if (typeof defaultValue === "object") {
    return defaultValue;
  }

  const suchOption = options.find((item) => {
    if (typeof item === "object" && item.value === defaultValue) {
      return item;
    }
    if (typeof item !== "object" && item === defaultValue) {
      return item;
    }
  });

  return suchOption || [];
};

export interface AutocompleteMultiSelectProps<
  M extends boolean | undefined = false,
  D extends boolean | undefined = false,
  F extends boolean | undefined = false
> extends Omit<AutocompleteProps<SingleValue, M, D, F>, "renderInput" | "onChange"> {
  label?: string;
  limitTags?: number;
  multiple?: M;
  onChange?: (
    value: M extends true
      ? AutocompleteValue<string | number, true, false, false>
      : AutocompleteValue<string | number, false, false, false>
  ) => void;
  selectAll?: boolean;
  disabledClean?: D;
  disabled?: boolean;
  onClear?: (state?: Array<string | number>) => void;
  stateAfterClear?: State | string | number | SelectOption;
  tagSize?: string;
  textFieldProps?: TextFieldProps;
  renderInput?: AutocompleteProps<SingleValue, M, D, F>["renderInput"];
}

export function AutocompleteMultiSelect<
  M extends boolean | undefined = false,
  D extends boolean | undefined = false,
  F extends boolean | undefined = false
>({
  options,
  label,
  limitTags = 2,
  // value,
  defaultValue,
  onChange,
  selectAll = true,
  disabled,
  disabledClean,
  multiple,
  stateAfterClear = [],
  sx,
  tagSize = "16px",
  textFieldProps,
  ...autoCompleteProps
}: AutocompleteMultiSelectProps<M, D, F>) {
  const inputRef = useRef<HTMLInputElement>();
  const [state, setState] = useState<State>(wrapInArray(setDefaultValue(options, defaultValue as null))); // TS issue

  const normalizedOptions = useMemo(
    () => options.filter((item) => !(typeof item === "object" && item.hasOwnProperty("role"))),
    [options]
  );

  const filterOptions = useCallback(
    (search?: string): State => {
      if (!search) return normalizedOptions;
      return normalizedOptions.filter((item) => {
        return typeof item === "object"
          ? String(item.value).toLowerCase().includes(search.toLowerCase())
          : String(item).toLowerCase().includes(search.toLowerCase());
      });
    },
    [inputRef.current, normalizedOptions]
  );

  const allSelected = Array.isArray(state) && normalizedOptions.length === state.length;

  useDidUpdate(() => {
    if (onChange) {
      if (multiple === true) {
        //@ts-ignore not sure how to handle this with conditional types
        onChange(state.map(toPrimitive));
      } else {
        //@ts-ignore not sure how to handle this with conditional types
        onChange(state[0] ? toPrimitive(state[0]) : null);
      }
    }
  }, [state]);

  const handleClearOptions = () => {
    setState(wrapInArray(stateAfterClear));
  };

  const mergeStateAndSearch = (searchResult: State): State => {
    const filteredSearch = searchResult.filter((searchItem) => {
      if (typeof searchItem === "object") {
        return (
          state.findIndex((stateItem) => {
            if (typeof stateItem === "object") {
              searchItem.value === stateItem.value;
            }
          }) < -1
        );
      }
      return !state.includes(searchItem);
    });

    return [...state, ...filteredSearch];
  };

  const handleSelectAll = (isAllSelected: boolean): void => {
    if (isAllSelected) {
      return handleClearOptions();
    }

    if (inputRef.current?.value) {
      return setState(mergeStateAndSearch(filterOptions(inputRef.current?.value)));
    }
    return setState(normalizedOptions);
  };

  const isSelectAll = (array: Array<SelectOption | string | number>): boolean => {
    return !!array.find((option) => typeof option === "object" && option.value === selectAllVar);
  };

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: Nullable<Value>, reason: AutocompleteChangeReason) => {
      if (reason === "selectOption" || reason === "removeOption") {
        if (isNull(value)) {
          return setState([]);
        }

        if (multiple && Array.isArray(value)) {
          if (isSelectAll(value)) {
            return handleSelectAll(allSelected);
          }
          return setState(value);
        }

        return setState(wrapInArray(value));
      } else if (reason === "clear") {
        handleClearOptions();
      }
    },
    [allSelected]
  );

  const filter = createFilterOptions<SelectOption | string | number>();

  return (
    <Autocomplete<SingleValue, M, D, F>
      multiple={multiple}
      disableClearable={disabledClean}
      ListboxProps={{ style: { fontSize: "16px", color: "#0a3142" } }}
      slotProps={{
        listbox: { sx: { "& .MuiAutocomplete-option:hover": { backgroundColor: "#f5f5f5" } } }
      }}
      disableCloseOnSelect={multiple}
      limitTags={limitTags}
      options={normalizedOptions}
      //@ts-ignore not sure how to handle this with conditional types
      value={multiple ? state : state[0]}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const selectAllOption: SelectOption = {
          key: selectAllVar,
          value: selectAllVar,
          text: "Select All"
        };

        return multiple && selectAll ? [selectAllOption, ...filtered] : filtered;
      }}
      renderOption={(props) => {
        return (
          <li {...props} key={props.id}>
            {(props as any)["key"]}
          </li>
        ); // THIS FIX FOR WRONG SEARCH FROM INPUT, NOW IT RENDER RIGHT SEARCH RESULT
      }}
      onChange={handleChange}
      getOptionLabel={(option) => {
        return typeof option === "object" ? option.text : String(option);
      }}
      sx={{
        ".MuiFormControl-root": {
          marginBottom: 0,
          minWidth: "120px",
          ".MuiOutlinedInput-root": {
            padding: "5px"
          },
          ".MuiChip-label": {
            color: "#0a3142",
            fontSize: "16px"
          },
          ".MuiInputLabel-root": {
            color: "#5d7782"
          },
          ".MuiAutocomplete-tag": {
            color: "#0a3142",
            fontSize: tagSize
          },
          ".MuiAutocomplete-root": {
            width: "100%"
          }
        },
        ...sx
      }}
      renderInput={(params) => <TextField inputRef={inputRef} {...params} {...textFieldProps} label={label} />}
      {...autoCompleteProps}
      disabled={disabled}
    />
  );
}
